import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Users, Truck, FileWarning, CalendarClock, Banknote, Building2, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";

const COLORS = ["#0284c7", "#06b6d4", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([
          api.get("/stats/dashboard"),
          api.get("/alerts/expiring?days=60"),
        ]);
        setStats(s.data);
        setAlerts(a.data);
      } catch {}
    })();
  }, []);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return t("goodMorning");
    if (h < 18) return t("goodAfternoon");
    return t("goodEvening");
  };

  const kpis = stats ? [
    { label: t("totalEmployees"), value: stats.total_employees, icon: Users, color: "from-sky-500 to-cyan-500", testid: "kpi-total" },
    { label: t("activeDrivers"), value: stats.drivers, icon: Truck, color: "from-amber-500 to-orange-500", testid: "kpi-drivers" },
    { label: t("expiringDocs"), value: stats.expiring_docs, icon: FileWarning, color: "from-rose-500 to-red-500", testid: "kpi-expiring" },
    { label: t("pendingLeaves"), value: stats.pending_leaves, icon: CalendarClock, color: "from-violet-500 to-fuchsia-500", testid: "kpi-leaves" },
    { label: t("monthlyPayroll"), value: new Intl.NumberFormat(lang === "tr" ? "tr-TR" : "en-US", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(stats.monthly_payroll || 0), icon: Banknote, color: "from-emerald-500 to-teal-500", testid: "kpi-payroll" },
  ] : [];

  const typeData = (stats?.types || []).map(x => ({ name: t(x.type) || x.type, value: x.count }));
  const deptData = (stats?.departments || []).slice(0, 6).map(x => ({ name: x.department || "—", count: x.count }));

  return (
    <div data-testid="dashboard" className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-mono-brand">{greeting()}, {user?.name}</p>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight">{t("overview")}</h1>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono-brand uppercase tracking-wider">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <Card key={i} data-testid={k.testid} className="p-5 border-border/60 card-hover relative overflow-hidden">
              <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10 bg-gradient-to-br ${k.color}`} />
              <Icon className="w-5 h-5 text-primary mb-3" />
              <div className="text-xs font-mono-brand uppercase tracking-wider text-muted-foreground mb-1">{k.label}</div>
              <div className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight">{k.value}</div>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Dept chart */}
        <Card className="p-5 border-border/60 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-bold">{t("departmentDist")}</h3>
              <p className="text-xs text-muted-foreground">{t("overview")}</p>
            </div>
            <Building2 className="w-4 h-4 text-muted-foreground" />
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={deptData}>
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
              <Bar dataKey="count" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 border-border/60">
          <h3 className="font-display font-bold mb-4">{t("typeDist")}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={typeData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80} paddingAngle={3}>
                {typeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Alerts */}
      <Card data-testid="alerts-card" className="p-5 border-border/60">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-bold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" /> {t("recentAlerts")}
            </h3>
            <p className="text-xs text-muted-foreground">{t("expiringDocs")}</p>
          </div>
        </div>
        {alerts.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">{t("noUpcoming")}</div>
        ) : (
          <div className="divide-y divide-border/60">
            {alerts.slice(0, 10).map((a, i) => (
              <Link
                to={`/employees/${a.employee_id}`}
                key={i}
                data-testid={`alert-${i}`}
                className="flex items-center justify-between py-2.5 gap-3 hover:bg-accent/50 -mx-2 px-2 rounded transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Badge variant={a.severity === "expired" ? "destructive" : a.severity === "critical" ? "destructive" : "secondary"}>
                    {t(a.severity) || a.severity}
                  </Badge>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{a.employee_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{a.label}</div>
                  </div>
                </div>
                <div className="text-xs font-mono-brand uppercase tracking-wider text-muted-foreground shrink-0">
                  {a.days_remaining < 0 ? t("expired") : `${a.days_remaining} ${t("expiresIn")}`}
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
